/**
 * Admin "Governance" panel — read-only mirror of the procurement-facing
 * governance data (company facts, primary data residency, sub-processor
 * register, retention schedule + DPA PDF). Sourced live from the public
 * `/api/public/governance/sub-processors.json` feed, so it always matches
 * exactly what a prospect's procurement team sees. Handy for sales reps
 * answering due-diligence questions on a call without leaving the console.
 */
import React, { useCallback, useEffect, useState } from "react";
import { RefreshCw, ShieldCheck, MapPin, Download, Copy, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { API_BASE } from "../../lib/api";

const DPA_PDF = `${API_BASE}/public/governance/dpa-summary.pdf`;
const JSON_FEED = `${API_BASE}/public/governance/sub-processors.json`;

const copy = (text, label) => {
  navigator.clipboard?.writeText(text).then(
    () => toast.success(`${label} copied`),
    () => toast.error("Copy failed"),
  );
};

const Fact = ({ label, value }) => (
  <div>
    <div className="text-[10.5px] tracking-[0.16em] uppercase ek-text-muted">{label}</div>
    <div className="text-[13.5px] ek-text mt-0.5">{value || "—"}</div>
  </div>
);

export default function GovernancePanel() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(JSON_FEED);
      setData(await res.json());
    } catch {
      toast.error("Failed to load governance data");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { reload(); }, [reload]);

  const c = data?.company || {};
  const subs = data?.sub_processors || [];
  const retention = data?.retention_schedule || [];

  return (
    <div data-testid="admin-governance-panel">
      <div className="flex items-center justify-between gap-4 mb-6">
        <div>
          <h2 className="ek-heading text-xl flex items-center gap-2">
            <ShieldCheck size={18} className="text-[#7737F6]" /> Governance &amp; data residency
          </h2>
          <p className="ek-text-muted text-[13px] mt-1 max-w-2xl">
            Exactly what a prospect&rsquo;s procurement team sees on the public sub-processors page — for answering due-diligence questions on a call. Read-only; edit in <code className="text-[11px]">backend/governance.py</code>.
          </p>
        </div>
        <button type="button" onClick={reload} disabled={loading} className="ek-text-muted hover:text-black text-[12px] flex items-center gap-1 disabled:opacity-50" data-testid="governance-refresh">
          <RefreshCw size={12} /> Refresh
        </button>
      </div>

      {loading ? (
        <div className="ek-text-muted text-[13px] py-8">Loading…</div>
      ) : (
        <div className="space-y-8">
          {/* Data residency banner */}
          <div className="flex items-start gap-3 border-l-2 border-[#7737F6] bg-[#7737F6]/5 p-4" data-testid="governance-residency">
            <MapPin size={18} className="text-[#7737F6] mt-0.5 shrink-0" />
            <div>
              <div className="text-[10.5px] tracking-[0.16em] uppercase ek-text-muted">Primary data residency</div>
              <div className="text-[15px] font-medium ek-text mt-0.5">{data?.hosting_region || "—"}</div>
            </div>
          </div>

          {/* Company facts */}
          <div className="border ek-line ek-surface p-5">
            <h3 className="text-[12px] font-semibold tracking-[0.18em] uppercase ek-text-muted mb-4">Controller / processor details</h3>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-x-6 gap-y-4">
              <Fact label="Legal entity" value={c.legal_name} />
              <Fact label="Company no." value={c.company_number} />
              <Fact label="VAT no." value={c.vat_number} />
              <Fact label="ICO registration" value={c.ico_registration} />
              <Fact label="Registered office" value={c.address} />
              <Fact label="Office (correspondence)" value={c.office_address} />
              <Fact label="Governing law" value={c.jurisdiction} />
              <Fact label="Data protection contact" value={c.contact_email} />
            </div>
          </div>

          {/* Sub-processor register */}
          <div>
            <h3 className="text-[12px] font-semibold tracking-[0.18em] uppercase ek-text-muted mb-3">Sub-processor register ({subs.length})</h3>
            <div className="border ek-line ek-surface overflow-x-auto">
              <table className="min-w-full text-[13px]">
                <thead className="ek-surface-2 text-left ek-text-muted text-[10.5px] uppercase tracking-[0.12em]">
                  <tr><th className="p-3">Sub-processor</th><th className="p-3">Purpose</th><th className="p-3">Data</th><th className="p-3">Region</th></tr>
                </thead>
                <tbody>
                  {subs.map((p) => (
                    <tr key={p.name} className="border-t ek-line align-top" data-testid={`governance-sub-${p.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                      <td className="p-3 font-medium ek-text whitespace-nowrap">{p.name}</td>
                      <td className="p-3 ek-text-soft">{p.purpose}</td>
                      <td className="p-3 ek-text-soft">{p.data}</td>
                      <td className="p-3 ek-text-soft whitespace-nowrap">{p.region}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Retention schedule */}
          <div>
            <h3 className="text-[12px] font-semibold tracking-[0.18em] uppercase ek-text-muted mb-3">Data-retention schedule</h3>
            <div className="border ek-line ek-surface overflow-x-auto">
              <table className="min-w-full text-[13px]">
                <thead className="ek-surface-2 text-left ek-text-muted text-[10.5px] uppercase tracking-[0.12em]">
                  <tr><th className="p-3">Data category</th><th className="p-3">Retention period</th><th className="p-3">Lawful basis</th></tr>
                </thead>
                <tbody>
                  {retention.map((r) => (
                    <tr key={r.category} className="border-t ek-line align-top">
                      <td className="p-3 font-medium ek-text">{r.category}</td>
                      <td className="p-3 ek-text-soft whitespace-nowrap">{r.period}</td>
                      <td className="p-3 ek-text-soft">{r.lawful_basis}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Shareable links */}
          <div className="flex flex-wrap items-center gap-3 border-t ek-line pt-6">
            <a href={DPA_PDF} target="_blank" rel="noreferrer noopener" className="ek-btn ek-btn-primary text-[12.5px] inline-flex items-center gap-2" data-testid="governance-download-dpa">
              <Download size={14} /> Download DPA summary (PDF)
            </a>
            <button type="button" onClick={() => copy("https://www.ekselens.com/sub-processors", "Sub-processors page link")} className="ek-text-soft hover:text-black text-[12px] border ek-line px-3 py-2 inline-flex items-center gap-1.5" data-testid="governance-copy-page">
              <Copy size={12} /> Copy procurement page link
            </button>
            <a href="https://www.ekselens.com/sub-processors" target="_blank" rel="noreferrer noopener" className="text-[#7737F6] hover:underline text-[12px] inline-flex items-center gap-1" data-testid="governance-open-page">
              Open public page <ExternalLink size={11} />
            </a>
          </div>
        </div>
      )}
    </div>
  );
}
